import { useSeo } from "../lib/seo";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageShell, Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";

export default function AuthPage() {
  useSeo({ title: "Sign in | DITHER", description: "Sign in to keep your DITHER history and favourites.", noindex: true });
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/account" replace />;

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success("You're in! Guest history synced to your account.");
      navigate("/account");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isLogin = mode === "login";
  return (
    <PageShell title={isLogin ? "Welcome back" : "Join DITHER"} subtitle="Optional — DITHER works fine without an account." testId="auth-page">
      <Panel>
        <div className="mb-5 grid grid-cols-2 gap-2 rounded-2xl border-2 border-ink p-1">
          {(["login", "register"] as const).map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }} data-active={mode === m} data-testid={`auth-tab-${m}`}
              className="rounded-xl py-2 font-display font-bold uppercase transition-colors data-[active=true]:bg-ink data-[active=true]:text-white">
              {m === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {!isLogin && <input className="field" placeholder="Your name" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" required />}
          <input className="field" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={isLogin ? "login-email-input" : "register-email-input"} required />
          <input className="field" type="password" placeholder="Password (6+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid={isLogin ? "login-password-input" : "register-password-input"} required minLength={isLogin ? 1 : 6} />
          {error && <p className="rounded-xl bg-pop-pink/10 p-3 text-sm font-bold text-[#BE123C]" data-testid="auth-error-message">{error}</p>}
          <PopButton type="submit" tone="orange" size="lg" className="w-full" disabled={busy} data-testid={isLogin ? "login-submit-button" : "register-submit-button"}>
            {busy ? "One sec…" : isLogin ? "Log in" : "Create account"}
          </PopButton>
        </form>
      </Panel>
    </PageShell>
  );
}
